import {
  Home, Compass, DraftingCompass, LayoutGrid, Globe2, Users, Mail, TrendingUp,
  Briefcase, ShieldCheck, Workflow, MessageSquareQuote, HelpCircle, Map as MapIcon,
  MapPin, Building, Flag, Factory, Target, BarChart3, Send, MessageCircle, AtSign,
  MapPinned, ListChecks, BookOpen,
} from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/services";
import { CAD_SERVICES, CAD_SERVICE_CATEGORIES } from "@/data/cadServices";
import { US_STATES, US_REGIONS, getStateBySlug } from "@/data/usLocations";
import { DISCIPLINES } from "@/data/disciplines";
import { surveyIcon, cadIcon } from "./navIcons";

export const SURVEY_GROUP = {
  id: "survey",
  align: "left",
  icon: Compass,
  title: "Land Survey CAD",
  landingTo: "/survey-techies",
  landingLabel: "Survey Techies",
  landingTestId: "mega-survey-techies-link",
  ctaLabel: "Hire a survey techie",
  categories: SERVICE_CATEGORIES,
  services: SERVICES,
  iconFor: surveyIcon,
  colTestIdPrefix: "mega-col",
  linkTestIdPrefix: "mega-link",
  href: (slug) => `/services/${slug}`,
  anchor: (slug) => `/survey-techies#svc-${slug}`,
};

export const CAD_GROUP = {
  id: "cad",
  align: "center",
  icon: DraftingCompass,
  title: "CAD Drafting (Beyond Survey)",
  landingTo: "/cad-techies",
  landingLabel: "CAD Techies",
  landingTestId: "mega-cad-techies-link",
  ctaLabel: "Hire a CAD techie",
  categories: CAD_SERVICE_CATEGORIES,
  services: CAD_SERVICES,
  iconFor: cadIcon,
  colTestIdPrefix: "mega-cad-col",
  linkTestIdPrefix: "mega-cad-link",
  href: (slug) => `/cad-techies#cad-${slug}`,
  anchor: (slug) => `/cad-techies#cad-${slug}`,
  footerLinks: DISCIPLINES.map((d) => ({ key: d.slug, label: d.short, to: `/disciplines/${d.slug}` })),
};

export const MEGA_GROUPS = [SURVEY_GROUP, CAD_GROUP];

export const TOTAL_SERVICE_COUNT = MEGA_GROUPS.reduce((sum, g) => sum + g.services.length, 0);

const HOME_COLUMNS = [
  {
    title: "Explore",
    items: [
      { key: "outcomes", label: "Client outcomes", desc: "Measurable results for survey firms", icon: TrendingUp, to: "/#home-outcomes" },
      { key: "services", label: "Service lines", desc: "The full survey-drafting stack", icon: LayoutGrid, to: "/#home-services" },
      { key: "engagement", label: "Engagement models", desc: "Three ways to work with us", icon: Briefcase, to: "/#home-engagement" },
      { key: "why", label: "Why choose us", desc: "Built by surveyors, for surveyors", icon: ShieldCheck, to: "/#home-why" },
    ],
  },
  {
    title: "Learn more",
    items: [
      { key: "workflow", label: "How we work", desc: "Four-step production pipeline", icon: Workflow, to: "/#home-workflow" },
      { key: "testimonials", label: "Testimonials", desc: "What survey principals tell us", icon: MessageSquareQuote, to: "/#home-testimonials" },
      { key: "coverage", label: "Where we work", desc: "USA · Canada · Australia", icon: Globe2, to: "/#home-coverage" },
      { key: "faq", label: "FAQ", desc: "Questions before signing", icon: HelpCircle, to: "/#home-faq" },
    ],
  },
];

const TOP_STATES = ["florida", "texas", "california", "new-york", "georgia", "arizona"]
  .map(getStateBySlug)
  .filter(Boolean);

const COVERAGE_COLUMNS = [
  {
    title: "US regions",
    items: US_REGIONS.map((r) => ({
      key: `region-${r.toLowerCase()}`,
      label: r,
      desc: `${US_STATES.filter((s) => s.region === r).length} states`,
      icon: MapIcon,
      to: `/cad-services#region-${r.toLowerCase()}`,
    })),
  },
  {
    title: "Top states",
    items: TOP_STATES.map((s) => ({
      key: `state-${s.slug}`,
      label: s.state,
      desc: `${s.abbr} · counties & ZIP coverage`,
      icon: MapPin,
      to: `/cad-services/${s.slug}`,
    })),
  },
  {
    title: "Metros & international",
    items: [
      { key: "metros", label: "Metro hubs", desc: "25 US metros we serve most", icon: Building, to: "/cad-services#metro-hubs" },
      { key: "canada", label: "Canada", desc: "Provinces & territories", icon: Flag, to: "/contact?country=Canada&state=Canada&code=CA" },
      { key: "australia", label: "Australia", desc: "States & territories", icon: Flag, to: "/contact?country=Australia&state=Australia&code=AU" },
    ],
  },
];

const ABOUT_COLUMNS = [
  {
    title: "Company",
    items: [
      { key: "story", label: "Our story", desc: "A multi-discipline CAD partner", icon: Users, to: "/about" },
      { key: "industries", label: "Industries we serve", desc: "Survey, AEC, MEP & specialty", icon: Factory, to: "/about#about-industries" },
      { key: "values", label: "Operating principles", desc: "How we run the studio", icon: Target, to: "/about#about-values" },
      { key: "snapshot", label: "Company snapshot", desc: "Tri-Axis XYZ at a glance", icon: BarChart3, to: "/about#about-snapshot" },
      { key: "blog", label: "Blog & buyer guides", desc: "Pricing, turnaround, standards", icon: BookOpen, to: "/blog" },
    ],
  },
];

const CONTACT_COLUMNS = [
  {
    title: "Get in touch",
    items: [
      { key: "brief", label: "Send a project brief", desc: "Reply within one business day", icon: Send, to: "/contact#contact-brief" },
      { key: "whatsapp", label: "WhatsApp · Call", desc: "+91 80836 91588", icon: MessageCircle, href: "https://wa.me/918083691588" },
      { key: "email", label: "Email us", desc: "triaxisxyzsolutions@gmail.com", icon: AtSign, href: "mailto:triaxisxyzsolutions@gmail.com" },
      { key: "map", label: "Office location", desc: "Orlando, Florida, USA", icon: MapPinned, to: "/contact#contact-map" },
      { key: "counties", label: "Florida county index", desc: "Counties & ZIP codes", icon: ListChecks, to: "/contact#contact-counties" },
    ],
  },
];

export const NAV = [
  { key: "home", to: "/", label: "Home", icon: Home, menu: { type: "links", align: "left", width: "w-[560px]", columns: HOME_COLUMNS } },
  { key: "survey-techies", to: "/survey-techies", label: "Survey Techies", icon: Compass, menu: { type: "group", group: SURVEY_GROUP } },
  { key: "cad-techies", to: "/cad-techies", label: "CAD Techies", icon: DraftingCompass, menu: { type: "group", group: CAD_GROUP } },
  { key: "services", to: "/services", label: "Services", icon: LayoutGrid, menu: { type: "mega" } },
  {
    key: "coverage", to: "/cad-services", label: "Coverage", icon: Globe2,
    menu: { type: "links", align: "center", width: "w-[780px]", columns: COVERAGE_COLUMNS, footer: { label: "Browse all 50 states", to: "/cad-services" } },
  },
  { key: "about", to: "/about", label: "About", icon: Users, menu: { type: "links", align: "right", width: "w-[340px]", columns: ABOUT_COLUMNS } },
  { key: "contact", to: "/contact", label: "Contact", icon: Mail, menu: { type: "links", align: "right", width: "w-[360px]", columns: CONTACT_COLUMNS } },
];
